/*
 * Sovyer: clicking a product video opens it centered on screen in a native <dialog>, with a
 * scale+fade entrance. The dialog is just an overlay on top of the page the whole time — when
 * the video finishes (or the visitor closes it), it fades/scales back out and whatever was
 * already behind it is simply what's left. See layout/theme.liquid for the .sovyer-video-lightbox
 * styles and the shared .dialog-modal dark-mode rules it reuses.
 *
 * Two separate video presentations exist in this theme, so two independent click handlers below:
 *
 * 1. The product page's own media gallery (`_product-media-gallery`) already renders a real
 *    <deferred-media> + poster-button that swaps in a genuine <video> tag on first click and
 *    plays it inline. We intercept that click, steal the resulting <video> element into our
 *    dialog instead of letting it play inline, and put it back where it came from when we're
 *    done (so a second click, or anything else that expects it in place, still works normally).
 *
 * 2. The homepage "Featured product" gallery (`_featured-product-gallery`) has no such
 *    machinery — a video's slide there is just a poster <img>, wrapped in a plain <a> that
 *    normally navigates straight to the product page on any click. For that case there's no
 *    existing <video> to steal, so we fetch the product's public /products/<handle>.js JSON,
 *    find the matching media by its slide-id, and build a fresh <video> from its sources.
 */
(function () {
  var dialog, panel, stage, activeVideo, ownsActiveVideo, originalParent, originalNextSibling, originalLoop, closing;

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'dialog-modal sovyer-video-lightbox';
    dialog.innerHTML =
      '<div class="sovyer-video-lightbox__panel">' +
        '<button type="button" class="sovyer-video-lightbox__close" aria-label="Close">' +
          '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M11.1765 12.025C11.4108 12.2594 11.7907 12.2594 12.025 12.025C12.2594 11.7907 12.2594 11.4108 12.025 11.1765L8.84931 8.00078L12.025 4.82505C12.2594 4.59073 12.2594 4.21083 12.025 3.97652C11.7907 3.7422 11.4108 3.7422 11.1765 3.97652L8.00078 7.15225L4.82505 3.97652C4.59073 3.7422 4.21083 3.7422 3.97652 3.97652C3.7422 4.21083 3.7422 4.59073 3.97652 4.82505L7.15225 8.00078L3.97652 11.1765C3.7422 11.4108 3.7422 11.7907 3.97652 12.025C4.21083 12.2594 4.59073 12.2594 4.82505 12.025L8.00078 8.84931L11.1765 12.025Z" fill="currentColor"></path>' +
          '</svg>' +
        '</button>' +
        '<div class="sovyer-video-lightbox__stage"></div>' +
      '</div>';
    document.body.appendChild(dialog);
    panel = dialog.querySelector('.sovyer-video-lightbox__panel');
    stage = dialog.querySelector('.sovyer-video-lightbox__stage');
    dialog.querySelector('.sovyer-video-lightbox__close').addEventListener('click', requestClose);
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) requestClose();
    });
    dialog.addEventListener('cancel', function (e) {
      // Escape key: intercept the native instant-close so we get the fade-out instead.
      e.preventDefault();
      requestClose();
    });
  }

  // isOwned = true when we created the <video> ourselves (featured-product case) and should
  // just discard it on close; false when we borrowed it from the page (product-gallery case)
  // and need to put it back exactly where we found it.
  function showVideo(video, isOwned) {
    if (!dialog) build();

    ownsActiveVideo = isOwned;
    if (!isOwned) {
      originalParent = video.parentElement;
      originalNextSibling = video.nextSibling;
      originalLoop = video.loop;
      video.loop = false;
    }

    video.setAttribute('controls', '');
    stage.appendChild(video);
    activeVideo = video;
    closing = false;

    dialog.showModal();
    // Double rAF: let the dialog paint in its base (closed-looking) state once before adding
    // .is-open, so the opacity/transform transition actually has something to animate from.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        dialog.classList.add('is-open');
      });
    });

    try {
      video.currentTime = 0;
    } catch (e) {
      /* ignore */
    }
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
    video.addEventListener('ended', onEnded);
  }

  function onEnded() {
    requestClose();
  }

  function requestClose() {
    if (!dialog || closing || !dialog.open) return;
    closing = true;
    dialog.classList.remove('is-open');
    if (activeVideo) {
      activeVideo.pause();
      activeVideo.removeEventListener('ended', onEnded);
    }
    // Matches the .sovyer-video-lightbox__panel transition duration in layout/theme.liquid.
    setTimeout(finishClose, 400);
  }

  function finishClose() {
    dialog.close();
    if (activeVideo) {
      var video = activeVideo;
      var owned = ownsActiveVideo;
      activeVideo = null;
      video.removeAttribute('controls');

      if (owned) {
        video.pause();
        video.removeAttribute('src');
        video.load();
        video.remove();
      } else {
        video.loop = originalLoop;
        var parent = originalParent;
        var sibling = originalNextSibling;
        if (parent) {
          if (sibling && sibling.parentNode === parent) {
            parent.insertBefore(video, sibling);
          } else {
            parent.appendChild(video);
          }
        }
      }
    }
    closing = false;
  }

  // --- 1. Product page gallery: steal the real <video> once deferred-media loads it ---
  document.addEventListener(
    'click',
    function (e) {
      var posterBtn = e.target.closest('.deferred-media__poster-button');
      if (!posterBtn) return;
      var deferredMedia = posterBtn.closest('deferred-media');
      if (!deferredMedia) return;

      e.preventDefault();
      e.stopPropagation();

      if (deferredMedia.getAttribute('data-media-loaded') !== 'true' && typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent();
      }

      var tries = 0;
      (function waitForVideo() {
        var video = deferredMedia.querySelector('video');
        if (video) {
          showVideo(video, false);
        } else if (tries++ < 180) {
          requestAnimationFrame(waitForVideo);
        }
      })();
    },
    true
  );

  // --- 2. Featured-product gallery: no <video> exists yet, build one from the product JSON ---
  document.addEventListener(
    'click',
    function (e) {
      var slide = e.target.closest('.product-media-container--video');
      if (!slide) return;
      // If this slide already has real video/deferred-media markup, handler 1 owns it.
      if (slide.querySelector('video, deferred-media')) return;

      var galleryLink = slide.closest('a[href*="/products/"]');
      if (!galleryLink) return;

      var mediaId = slide.getAttribute('slide-id');
      var handleMatch = galleryLink.pathname.match(/\/products\/([^/?#]+)/);
      var handle = handleMatch && handleMatch[1];
      if (!mediaId || !handle) return;

      e.preventDefault();
      e.stopPropagation();

      fetch('/products/' + handle + '.js')
        .then(function (r) {
          return r.json();
        })
        .then(function (product) {
          var media = (product.media || []).filter(function (m) {
            return String(m.id) === String(mediaId) && m.media_type === 'video';
          })[0];
          if (!media || !media.sources || !media.sources.length) return;

          var mp4Sources = media.sources.filter(function (s) {
            return s.mime_type === 'video/mp4';
          });
          var pool = mp4Sources.length ? mp4Sources : media.sources;
          var best = pool.slice().sort(function (a, b) {
            return (b.width || 0) - (a.width || 0);
          })[0];
          if (!best) return;

          var video = document.createElement('video');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          if (media.preview_image && media.preview_image.src) {
            video.setAttribute('poster', media.preview_image.src);
          }
          var source = document.createElement('source');
          source.src = best.url;
          source.type = best.mime_type;
          video.appendChild(source);

          showVideo(video, true);
        })
        .catch(function () {
          /* fall through silently — worst case the click just did nothing */
        });
    },
    true
  );
})();
